import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ReactMarkdown from 'react-markdown';
import { FaArrowLeft } from 'react-icons/fa';
import Seo from '../components/ui/Seo';
import BlurFade from '../components/ui/BlurFade';
import { posts } from '../data/blog';

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

const markdownComponents = {
  h2: ({ children, ...props }) => <h2 className="mb-3 mt-8 text-2xl font-black" {...props}>{children}</h2>,
  p: ({ children, ...props }) => <p className="mb-4 leading-relaxed text-black/70" {...props}>{children}</p>,
  em: ({ children, ...props }) => <em className="text-black/50" {...props}>{children}</em>,
  strong: ({ children, ...props }) => <strong className="font-black" {...props}>{children}</strong>,
  ul: ({ children, ...props }) => <ul className="mb-4 list-disc pl-5 text-black/70" {...props}>{children}</ul>,
  li: ({ children, ...props }) => <li className="mb-1" {...props}>{children}</li>,
  a: ({ children, ...props }) => (
    <a className="font-semibold underline" target="_blank" rel="noopener noreferrer" {...props}>{children}</a>
  ),
};

const BlogPost = () => {
  const { slug } = useParams();
  const post = posts.find((p) => p.slug === slug);

  if (!post) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-4 bg-white px-6 text-center text-black">
        <Helmet>
          <title>Post not found | Prathamesh Shete</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <h1 className="text-3xl font-black">Post not found</h1>
        <p className="text-black/60">This blog post doesn't exist or may have been moved.</p>
        <Link to="/blog" className="mt-2 flex items-center gap-2 font-semibold underline">
          <FaArrowLeft size={12} /> Back to blog
        </Link>
      </main>
    );
  }

  return (
    <main className="bg-white pb-20 pt-32 text-black">
      <Seo
        title={`${post.title} | Prathamesh Shete`}
        description={post.excerpt}
        path={`/blog/${post.slug}`}
      />

      <div className="mx-auto max-w-2xl px-6">
        <BlurFade>
          <Link to="/blog" className="mb-8 flex w-fit items-center gap-2 text-sm font-semibold text-black/60 hover:text-black">
            <FaArrowLeft size={12} /> Back to blog
          </Link>
        </BlurFade>

        <BlurFade delay={0.05}>
          <span className="text-xs font-semibold text-black/40">{formatDate(post.date)}</span>
          <h1 className="mb-4 mt-2 text-3xl font-black leading-tight sm:text-4xl">{post.title}</h1>
          <div className="mb-8 flex flex-wrap gap-1.5">
            {post.tags.map((tag) => (
              <span key={tag} className="rounded-full border border-black/20 px-2 py-0.5 text-xs text-black/60">
                {tag}
              </span>
            ))}
          </div>
        </BlurFade>

        <BlurFade delay={0.1}>
          <ReactMarkdown components={markdownComponents}>{post.content}</ReactMarkdown>
        </BlurFade>
      </div>
    </main>
  );
};

export default BlogPost;
